import React, { useRef, useCallback, useEffect } from 'react';
import { Tree } from 'components/Tree/CommonTree/Tree';
import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import { getEntityActions, IEntityPickerTreeModel } from './defaultEpickTreeModel';
import { ICancelablePromise, makeCancelable } from 'utils/PromiseUtils';
import { EntityPickerItem } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/EntityPickerItem';
import { FavoriteEntityMessage, FavoriteType } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder';
import { generateGuid } from 'utils/Guid';
import { useCreateTreeStore } from 'components/Tree/CommonTree/useTreeStore';
import { EpickTreeConstants } from './entityPickerFetcher';
import { subScribeToEvent, unSubScribeToEvent } from 'utils/ClientEventUtils';
import { FavoriteEventId } from './EpickUtils';

export interface IEpickTreeProps {
  model: IEntityPickerTreeModel;
  onNodeSelected?: (node: IEpickNode) => void;
  onNodeMouseDown?: (node: ITreeNode<IEpickNode>, e: React.MouseEvent) => Promise<void> | void;
  onNodeKeyDown?: (node: ITreeNode<IEpickNode>, e: React.KeyboardEvent) => Promise<void> | void;
  /**
   * Runs on initial load when all root nodes are loaded and when their opened children are loaded as well.
   */
  onTreeLoaded?: (model?: ITreeNode<IEpickNode>[]) => void;
  treeId?: string;
}

export interface IEpickNode {
  id: string;
  name: string;
  pickable?: boolean;
  canBeFav?: boolean;
  isFav?: boolean;
  favoriteType?: FavoriteType;
  shouldShowFavoritesMenu?: boolean;
  note?: string;
  expandable?: boolean;
  expanded?: boolean;
  order?: number;
  entity?: EntityPickerItem;
  title?: string;
}

export function EpickTree({
  model,
  onNodeMouseDown,
  onNodeSelected,
  onTreeLoaded,
  onNodeKeyDown,
  treeId,
}: IEpickTreeProps) {
  const id = useRef(treeId || generateGuid()); // used to track event subscriptions
  const loadTreeRef = useRef<ICancelablePromise<ITreeNode<IEpickNode>[]> | null>(null);
  // callbacks that use state manager will use the old model when new model is provided via props, so we use `useRef` here instead of `useState`
  const modelRef = useRef<IEntityPickerTreeModel>(model);

  const loadTree = useCallback(async () => {
    try {
      // if model has been changed we need to cancel previous request, as it can override data of new one
      // when it takes more time
      loadTreeRef.current?.cancel();
      loadTreeRef.current = makeCancelable(modelRef.current.getRootNodes());

      let nodes = await loadTreeRef.current.promise;

      return nodes;
    } catch (error) {
      if (!(error as any).isCanceled) {
        console.error(error);
      }
    }
  }, []);

  const getNodes = useCallback(
    async (node?: ITreeNode<IEpickNode>): Promise<ITreeNode<IEpickNode>[]> => {
      if (!node?.data) {
        return (await loadTree()) ?? [];
      }
      let nodes = await modelRef.current.getSubNodes(node.data.id);

      return nodes;
    },
    [loadTree]
  );

  const useStore = useCreateTreeStore({
    treeId: id.current,
    nodes: [],
    loadNodes: getNodes,
    onNodeSelected: async node => {
      expandNode(node.nodeId);
      if (node?.data) {
        onNodeSelected?.(node.data);
      }
    },
    sortNodes: modelRef.current.sortNodes
      ? (nodes: ITreeNode<IEpickNode>[]) => {
          return nodes.sort(modelRef.current.sortNodes);
        }
      : undefined,
    onTreeLoaded: model => {
      onTreeLoaded?.(model.nodes);
    },
  });

  const nodes = useStore(s => s.nodes);
  const {
    expandNode,
    foldNode,
    selectNode,
    findNodes,
    updateNodes,
    updateNode,
    deleteNode,
    unshiftNode,
    loadTree: reloadTree,
    initialize,
  } = useStore(s => s.actions);
  const isLoading = useStore(s => s.isLoading);

  const reloadModel = useCallback(async () => {
    if (modelRef.current !== model) {
      modelRef.current = model;
      await reloadTree();
      await initialize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  useEffect(() => {
    reloadModel();
  }, [model, reloadModel]);

  const refreshFavoriteNode = useCallback(async () => {
    const oldFavorite = findNodes(node => {
      return node?.data?.id === EpickTreeConstants.favoriteItemsFolderId;
    })[0];

    const favoriteNode = await model.getFavoritesRootNode?.();
    if (favoriteNode?.length) {
      if (!!oldFavorite) {
        favoriteNode[0].nodeId = oldFavorite.nodeId;
        updateNode(favoriteNode[0]);
      } else {
        unshiftNode(favoriteNode[0]);
      }
    } else {
      deleteNode(oldFavorite?.nodeId);
    }
  }, [deleteNode, model, findNodes, updateNode, unshiftNode]);

  const handleFavoriteUpdate = useCallback(
    async (e: CustomEvent<FavoriteEntityMessage>) => {
      if (e.detail?.Entity) {
        const message = e.detail;

        let nodes = findNodes(node => {
          return (
            node?.data?.id === message.Entity.ReferenceId && node?.data?.id !== EpickTreeConstants.favoriteItemsFolderId
          );
        });

        nodes.forEach(n => {
          if (n.data) {
            n.data.favoriteType = message.Entity.FavoriteType;
            n.actions = getEntityActions(n.data);
          }
        });

        updateNodes(nodes);
        await refreshFavoriteNode();
      }
    },
    [findNodes, refreshFavoriteNode, updateNodes]
  );

  useEffect(() => {
    const treeId = id.current;
    subScribeToEvent(FavoriteEventId, treeId, handleFavoriteUpdate);
    return () => {
      unSubScribeToEvent(FavoriteEventId, treeId, handleFavoriteUpdate);
    };
  }, [handleFavoriteUpdate]);

  return (
    <Tree
      searchString={model.search}
      onNodeMouseDown={onNodeMouseDown}
      onNodeKeyDown={onNodeKeyDown}
      onNodeExpand={expandNode}
      onNodeFold={foldNode}
      onNodeSelect={selectNode}
      classes={{ root: 'dpc-epick-tree', node: 'dpc-epick-tree__node' }}
      nodes={nodes}
      loading={{ isLoading, height: 330 }}
    />
  );
}
